<script setup lang="ts">
import { computed, reactive, ref, onMounted, watch, type Ref } from 'vue';
import { useRoute } from 'vue-router';
import KpFooter from '@/components/KpFooter.vue';
import { globalVar, useGlobals } from '@/helpers/globals';
import { useAuthUser, isAuthenticated } from '@/helpers/globals';
import { appUrl, apiUrl } from '@/helpers/api';
import { logout } from '@/helpers/auth';
import { RouterLink } from 'vue-router';
import { login } from '@/helpers/auth';
import LoginDialog from '@/components/LoginDialog.vue';
import ChangeRoleDialog from '@/components/ChangeRoleDialog.vue';
import AccountNavigationAuth from '@/components/AccountNavigationAuth.vue';
import AccountNavigationUnauth from '@/components/AccountNavigationUnauth.vue';
import NavBreadcrumbs from '@/components/NavBreadcrumbs.vue';
import { useBrowserSession } from '@kp/geofy-vuejs-lib/tools/browser_session.js';
import { on as onGlobalEvent } from './composables/useGlobalEventBus';
import '@kanton-thurgau/design-system/modal';
import { KTGModalElement } from '@kanton-thurgau/design-system/modal';

const globals = useGlobals();
const route = useRoute();
const browserSession = useBrowserSession('eplan');

const model = reactive({
    loading: false,
    error: null as string | null,
    showLoginDialog: false as boolean,
    showRoleDialog: false as boolean
});

const authUser = useAuthUser();
const username = computed(() => authUser?.name ?? '-');
const loginFail = computed(() => {
    return route.query.loginFail;
});
const logoutUrl: Ref<string | null> = ref(null);
let afterLoginRedirectUrl: string | null = null;

const items = globals.show_under_development_info
    ? ref([])
    : authUser?.active_role === 'admin'
      ? ref([
            {
                label: 'Planungsgeschäfte',
                itemId: 'planungsgeschaefte',
                route: { name: 'planungsgeschaefte-overview', params: {} }
            },
            {
                label: `Administration`,
                itemId: 'administration',
                route: { name: 'admin-users', params: {} }
            },
            {
                label: `Hilfe & Kontakt`,
                itemId: 'help',
                route: { name: 'help-faq', params: {} }
            }
        ])
      : ref([
            {
                label: 'Planungsgeschäfte',
                itemId: 'planungsgeschaefte',
                route: { name: 'planungsgeschaefte-overview', params: {} }
            },
            {
                label: `Hilfe & Kontakt`,
                itemId: 'help',
                route: { name: 'help-faq', params: {} }
            }
        ]);

// Register global login event handling:
onGlobalEvent('login', (redirectUrl: string) => {
    const loginMethod = globalVar('login_method');
    if (loginMethod === 'oidc') {
        oidcLogin(redirectUrl);
    } else {
        afterLoginRedirectUrl = redirectUrl;
        onLoginBtn();
    }
});

onMounted(() => {
    /*const loginFail = route.query.loginFail;
    console.log(route.query);
    if (loginFail) {
        logoutUrl.value = apiUrl('/auth/logout') + '?oidc=1';
        console.log('no gemeinde');
    }*/
    watch(loginFail, () => {
        const modal = document.getElementById('dangermouse')! as KTGModalElement;
        logoutUrl.value = apiUrl('/auth/logout') + '?oidc=1';
        modal.open();
    });
});

function showOldSchoolLogin() {
    const env = globals.environment;
    return !!(env === 'dev' || (env === 'test' && route.query.enableBasicAuth));
}

function onLoginBtn() {
    model.showLoginDialog = true;
}

function onChangeRoleBtn() {
    model.showRoleDialog = true;
}

function onLegacyUiBtn() {
    window.location.href = "/version_pilot"
}

async function onRoleChange(role: string) {
    if (!authUser) {
        return;
    }
    const oldVal = authUser.active_role;
    try {
        if (role.split('::').length === 2) {
            authUser.active_role = role.split('::')[0];
            authUser.active_role_stelle_id = parseInt(role.split('::')[1], 10);
        } else {
            authUser.active_role = role;
            authUser.active_role_stelle_id = null;
        }

        await authUser.save();
        model.showRoleDialog = false;
        browserSession.clearAllSessionStorage();
        document.location.href = appUrl('/');
    } catch (e) {
        console.error(e);
        authUser.active_role = oldVal;
        //this.$toast({ message: errToStr(e), color: 'error' });
    }
}

async function onLogin({ username, password }: { username: string; password: string }) {
    model.loading = true;
    model.error = null;
    try {
        await login(username, password);
        document.location.href = afterLoginRedirectUrl || appUrl('/');
    } catch (e) {
        console.error(e);
        model.error = 'Username oder Passwort falsch';
    } finally {
        model.loading = false;
    }
}

function oidcLogin(redirectUrl: string = appUrl('/')) {
    document.location.href = `${apiUrl('/oidc/login')}?url=${redirectUrl}`;
}

function login2() {
    document.location.href =
        apiUrl('/oidc2/login') +
        '?url=' +
        appUrl('/') +
        '&acr_values=urn%3Acoreone%3Aauthentication%3Alot%3Avalue%3Aurn%3Akttg%3Aauthentication%3Alot%3A2';
}

function onLogoutBtn() {
    onLogout();
}

async function onLogout() {
    model.loading = true;
    model.error = null;
    try {
        await logout();
        //document.location.href = appUrl('/', { logout: true });
    } catch (e) {
        console.error(e);
        model.error = 'Etwas ist schief gelaufen!';
    } finally {
        model.loading = false;
    }
}

function closeDangerMouse() {
    const modal = document.getElementById('dangermouse')! as KTGModalElement;
    modal.close();
}

</script>
<template>
    <ktg-horizon></ktg-horizon>
    <div class="sticky-header">
        <!-- eslint-disable vue/no-deprecated-slot-attribute -->
        <ktg-main-navigation :home-url="globals.frontend_base" logo-description="Zur Startseite">
            <!-- Meta navigation, top light gray area -->
            <ktg-horizontal-navigation label="Meta-Navigation" slot="meta">
                <ktg-meta-navigation-item href="https://www.tg.ch"> Startseite tg.ch </ktg-meta-navigation-item>
            </ktg-horizontal-navigation>

            <!-- Primary navigation -->
            <ktg-horizontal-navigation
                v-if="!globals.show_under_development_info"
                label="Hauptnavigation"
                slot="primary"
            >
                <template v-for="item in items" :key="item.itemId">
                    <RouterLink :to="item.route" v-slot="{ isActive }">
                        <ktg-horizontal-navigation-item :selected="isActive">
                            {{ item.label }}
                        </ktg-horizontal-navigation-item>
                    </RouterLink>
                </template>
            </ktg-horizontal-navigation>

            <AccountNavigationAuth
                v-if="!globals.show_under_development_info && isAuthenticated()"
                :username="username"
                @logout="onLogoutBtn"
                @changerole="onChangeRoleBtn"
                @legacyui="onLegacyUiBtn"
                @login2="login2"
            />
            <AccountNavigationUnauth
                v-if="!globals.show_under_development_info && !isAuthenticated()"
                :show-local-login="showOldSchoolLogin()"
                @login="onLoginBtn"
                @oidcLogin="oidcLogin"
            />
            <ktg-hamburger></ktg-hamburger>
        </ktg-main-navigation>

        <!-- eslint-enable vue/no-deprecated-slot-attribute -->
    </div>

    <nav class="ktg-main-container mt-1_5rem mb-4_5rem">
        <NavBreadcrumbs />
    </nav>

    <RouterView></RouterView>

    <KpFooter class="footer" />

    <LoginDialog
        v-model="model.showLoginDialog"
        :loading="model.loading"
        modal
        :header="$t('login')"
        :style="{ width: '25rem' }"
        :error="model.error"
        @login="onLogin"
        @cancel="model.showLoginDialog = false"
    />

    <ChangeRoleDialog
        v-model="model.showRoleDialog"
        :loading="model.loading"
        modal
        :style="{ width: '25rem' }"
        @rolechange="onRoleChange"
        @cancel="model.showRoleDialog = false"
    />

    <ktg-modal id="dangermouse" appearance="danger">
        <h2 class="ktg-modal-title"> {{ $t('fehlende_berechtigung') }} </h2>

        <p class="ktg-modal-text"> {{  $t('no_gemeinde_loging_msg1') }}</p>
        <p class="ktg-modal-text"> {{  $t('no_gemeinde_loging_msg2') }}</p>
        <p class="ktg-modal-text"> {{  $t('no_gemeinde_loging_msg3') }}</p>

        <ktg-modal-buttons vertical>
            <ktg-button @click="closeDangerMouse" class="cancel-button" appearance="primary" size="small" full-width> {{ $t('confirm') }} </ktg-button>
        </ktg-modal-buttons>
    </ktg-modal>

    <iframe v-if="logoutUrl" :src="logoutUrl" width="0" height="0" style="display: none" />
</template>

<style scoped lang="scss">
.sticky-header {
    position: sticky;
    top: 0;
    z-index: var(--ktg-layer-main-navigation);
}
</style>
